// contexts/AuthContext.js
'use client';

import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useRouter } from 'next/navigation';
import toast from 'react-hot-toast';
import { normalizeKeys, isSuccessResponse, getErrorMessage, readApiJson } from '@/utils/apiHelpers';
import { onSessionEnded, resetSessionEnded } from '@/utils/sessionExpiry';
import { AccountTypes, OrgAccountTypes } from '@/utils/builtInApplets';

import { logger } from '@/utils/log';
const AuthContext = createContext({});

// Safe JSON parse helper
const safeJsonParse = (str, fallback = null) => {
    if (!str) return fallback;
    try {
        return JSON.parse(str);
    } catch (error) {
        logger.warn('[Auth] Failed to parse JSON:', error);
        return fallback;
    }
};

export function AuthProvider({ children }) {
    const router = useRouter();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isAuthenticating, setIsAuthenticating] = useState(false);
    const [authKey, setAuthKey] = useState('');
    const [organizations, setOrganizations] = useState([]);
    const [currentOrg, setCurrentOrg] = useState(null);
    const [currentHouse, setCurrentHouse] = useState(null);
    const [roles, setRoles] = useState([]);
    const [orgApplets, setOrgApplets] = useState([]);
    const [appletsLoading, setAppletsLoading] = useState(false);
    const [orgSelectionError, setOrgSelectionError] = useState(null);

    // Use ref to track if initial auth check has completed
    const authCheckComplete = useRef(false);

    // Check for stored auth on mount
    useEffect(() => {
        if (!authCheckComplete.current) {
            authCheckComplete.current = true;
            checkStoredAuth();
        }
    }, []);

    // Check sessionStorage for existing auth (clears on browser close)
    const checkStoredAuth = async () => {
        logger.log('[Auth] Checking stored auth...');

        try {
            const storedAuthKey = sessionStorage.getItem('authKey');
            const storedUserId = sessionStorage.getItem('userId');

            // If we have auth credentials, authenticate IMMEDIATELY
            if (storedAuthKey && storedUserId) {
                logger.log('[Auth] Found stored credentials, authenticating...');

                // Set auth state FIRST - this is critical
                setAuthKey(storedAuthKey);
                setUser({ id: storedUserId });
                setIsAuthenticated(true);

                // Now load additional data (org, house, applets) - these are non-blocking
                const storedOrg = sessionStorage.getItem('currentOrg');
                const storedHouse = sessionStorage.getItem('currentHouse');
                const storedApplets = sessionStorage.getItem('orgApplets');
                const storedOrgs = sessionStorage.getItem('organizations');

                // Restore the full organizations list so /memberships can render it
                // after a reload/hot-refresh (state alone doesn't survive that).
                const orgs = safeJsonParse(storedOrgs, []);
                if (orgs.length > 0) {
                    logger.log('[Auth] Restoring organizations:', orgs.length);
                    setOrganizations(orgs);
                }

                // Load org data safely
                const org = safeJsonParse(storedOrg);
                if (org) {
                    logger.log('[Auth] Restoring org:', org.orgName || org.name);
                    setCurrentOrg(org);

                    // Load applets safely: the cached list shows at once, then the
                    // server's list replaces it. Without the refetch a reload kept
                    // an applet deleted elsewhere (another tab, a device, an AI job
                    // delete) in the sidebar for the whole session ("Rosters",
                    // 2026-10-28).
                    const applets = safeJsonParse(storedApplets, []);
                    if (applets.length > 0) {
                        logger.log('[Auth] Restoring cached applets:', applets.length);
                        setOrgApplets(applets);
                    }
                    if (org.orgId) {
                        // Fetch applets in background - don't await
                        logger.log('[Auth] Fetching applets in background...');
                        fetchOrgApplets(storedAuthKey, org.orgId).catch(err => {
                            logger.warn('[Auth] Background applet fetch failed:', err);
                        });
                    }

                    // Roles live only in React state and are otherwise populated
                    // just once, in selectOrganization. On a reload or a direct
                    // page load they'd stay empty, breaking any applet that
                    // gates on role-based access. Refetch them here.
                    if (org.orgId) {
                        logger.log('[Auth] Fetching roles in background...');
                        getOrgRoles(org.orgId).catch(err => {
                            logger.warn('[Auth] Background roles fetch failed:', err);
                        });
                    }
                }

                // Load house data safely
                const house = safeJsonParse(storedHouse);
                if (house) {
                    logger.log('[Auth] Restoring house:', house.stationName || house.StationName);
                    setCurrentHouse(house);
                    // Deliberately NOT seeding isHouseAdmin here. This house came
                    // from sessionStorage, which `selectHouse` also writes — so it
                    // proves the user PICKED this house, not that they administer
                    // it. The effect verifies with the server instead.
                }

                logger.log('[Auth] Auth restoration complete');
            } else {
                logger.log('[Auth] No stored credentials found');
            }
        } catch (error) {
            logger.error('[Auth] Error checking stored auth:', error);
            // Don't clear auth on error - just log it
        } finally {
            // Always complete loading
            setLoading(false);
            logger.log('[Auth] Loading complete, isAuthenticated:', isAuthenticated);
        }
    };

    // Fetch organization applets
    const fetchOrgApplets = useCallback(async (key, orgId) => {
        logger.log('[Auth] fetchOrgApplets called with:', { key: key ? 'present' : 'missing', orgId });

        if (!key || !orgId) {
            logger.log('[Auth] fetchOrgApplets - missing key or orgId, aborting');
            return { success: false, error: 'Missing key or orgId' };
        }

        setAppletsLoading(true);
        try {
            logger.log('[Auth] Fetching applets for org:', orgId);
            const response = await fetch('/api/auth/getorgapplets', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    Key: key,
                    OrgId: orgId
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const rawData = await response.json();
            logger.log('[Auth] getorgapplets raw response:', rawData);
            const data = normalizeKeys(rawData);
            logger.log('[Auth] getorgapplets normalized response:', data);

            if (isSuccessResponse(data)) {
                const applets = data.applets || [];
                logger.log('[Auth] Applets loaded:', applets.length, 'applets');
                setOrgApplets(applets);
                sessionStorage.setItem('orgApplets', JSON.stringify(applets));
                return { success: true, applets };
            } else {
                logger.error('[Auth] Get applets failed:', data);
                return {
                    success: false,
                    error: getErrorMessage(data, 'Failed to load applets')
                };
            }
        } catch (error) {
            logger.error('[Auth] Get applets error:', error);
            return {
                success: false,
                error: 'Network error. Please try again.'
            };
        } finally {
            setAppletsLoading(false);
        }
    }, []);

    // Refresh applets (can be called manually)
    const refreshApplets = useCallback(async () => {
        if (authKey && currentOrg?.orgId) {
            return await fetchOrgApplets(authKey, currentOrg.orgId);
        }
        return { success: false, error: 'Not authenticated or no org selected' };
    }, [authKey, currentOrg?.orgId, fetchOrgApplets]);

    // Login function
    const login = async (credentials) => {
        setIsAuthenticating(true);

        try {
            const requestPayload = {
                Id: credentials.username,
                Pw: credentials.password,
                AppId: 'FireOps Portal',
                Platform: 'WEB',
                Device: 'undefined',
                Ip: 'undefined'
            };

            const response = await fetch('/api/auth/requestloginkey', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestPayload)
            });

            const responseText = await response.text();

            let rawData;
            try {
                rawData = JSON.parse(responseText);
            } catch (e) {
                logger.error('Failed to parse response:', e);
                return {
                    success: false,
                    error: 'Invalid server response'
                };
            }

            const data = normalizeKeys(rawData);

            if (isSuccessResponse(data)) {
                if (!data.twoFactorRequired) {
                    // No 2FA required, proceed with getting credentials
                    const key = data.key;
                    setAuthKey(key);
                    sessionStorage.setItem('authKey', key);
                    sessionStorage.setItem('userId', credentials.username);
                    setUser({ id: credentials.username });
                    await getPortalCredentials(key);
                    return { success: true };
                } else {
                    // 2FA required - store temp key for 2FA submission
                    const key = data.key;
                    setAuthKey(key);
                    // Also store userId temporarily for 2FA flow
                    sessionStorage.setItem('pendingUserId', credentials.username);
                    return {
                        success: true,
                        twoFactorRequired: true,
                        twoFactorMethod: data.twoFactorMethod,
                        availableMethods: data.availableMethods
                    };
                }
            } else {
                logger.error('Login failed:', data);
                return {
                    success: false,
                    error: getErrorMessage(data, 'Login failed')
                };
            }
        } catch (error) {
            logger.error('Login error:', error);
            return {
                success: false,
                error: 'Network error. Please try again.'
            };
        } finally {
            setIsAuthenticating(false);
        }
    };

    // Submit 2FA code
    const submitTwoFactorCode = async (code) => {
        try {
            const response = await fetch('/api/auth/submittwofactorreply', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    Key: authKey,
                    PassCode: code
                })
            });

            const rawData = await response.json();
            const data = normalizeKeys(rawData);

            if (isSuccessResponse(data)) {
                const newKey = data.key;
                setAuthKey(newKey);
                sessionStorage.setItem('authKey', newKey);

                // Get pending userId from 2FA flow
                const pendingUserId = sessionStorage.getItem('pendingUserId');
                if (pendingUserId) {
                    sessionStorage.setItem('userId', pendingUserId);
                    setUser({ id: pendingUserId });
                    sessionStorage.removeItem('pendingUserId');
                }

                await getPortalCredentials(newKey);
                return { success: true };
            } else {
                return {
                    success: false,
                    error: getErrorMessage(data, 'Invalid code')
                };
            }
        } catch (error) {
            logger.error('2FA error:', error);
            return {
                success: false,
                error: 'Network error. Please try again.'
            };
        }
    };

    // Fetch the organizations list, update state + sessionStorage, and return it.
    // No routing or logout side effects — safe to call on mount (e.g. from the
    // /memberships page) to refresh the cached list. Falls back to the provided
    // key, then state, then sessionStorage.
    const refreshOrganizations = useCallback(async (key) => {
        const currentKey = key || authKey || sessionStorage.getItem('authKey');
        if (!currentKey) {
            return { success: false, error: 'Not authenticated' };
        }

        try {
            const response = await fetch('/api/auth/getportalcredentials', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ Key: currentKey })
            });

            const rawData = await response.json();
            const data = normalizeKeys(rawData);

            if (isSuccessResponse(data)) {
                const orgs = data.organizations || [];
                logger.log('[Auth] Organizations loaded:', orgs.map(o => ({
                    name: o.orgName,
                    id: o.orgId,
                    accountType: o.accountType,
                    orgAccountType: o.orgAccountType
                })));
                setOrganizations(orgs);
                sessionStorage.setItem('organizations', JSON.stringify(orgs));
                return { success: true, organizations: orgs };
            } else {
                logger.error('[Auth] Get credentials failed:', data);
                return {
                    success: false,
                    error: getErrorMessage(data, 'Failed to get credentials')
                };
            }
        } catch (error) {
            logger.error('Get credentials error:', error);
            return {
                success: false,
                error: 'Network error. Please try again.'
            };
        }
    }, [authKey]);

    // Get portal credentials (organizations) at login time — fetches then routes.
    const getPortalCredentials = async (key) => {
        try {
            const result = await refreshOrganizations(key);

            if (result.success) {
                const orgs = result.organizations;

                // Mark as authenticated now that we have credentials
                setIsAuthenticated(true);

                if (orgs.length === 1) {
                    // Auto-select if only one org and route based on account type
                    await selectOrganization(orgs[0]);
                } else if (orgs.length > 1) {
                    // Multiple orgs - go to memberships WITHOUT selecting anything
                    router.push('/memberships');
                } else {
                    // No organizations found
                    logger.warn('No organizations found for user');
                    router.push('/memberships');
                }

                return { success: true };
            } else {
                return result;
            }
        } catch (error) {
            logger.error('Get credentials error:', error);
            logout();
            return {
                success: false,
                error: error.message
            };
        }
    };

    // Get house data for SharedHouse accounts
    const getHouse = async (org) => {
        try {
            const currentKey = authKey || sessionStorage.getItem('authKey');
            const requestData = {
                Key: currentKey,
                OrgId: org.orgId,
                HouseId: org.sharedPermissionId
            };

            logger.debug('[Auth] getHouse request');       // never the body: it carries the session Key

            const response = await fetch('/api/portal/firehouse/GetFireHouse', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestData)
            });

            const rawData = await response.json();
            const data = normalizeKeys(rawData);

            logger.log('[Auth] getHouse response:', data);

            if (isSuccessResponse(data)) {
                // House can be null - that's OK, the page will handle it
                const house = data.house;
                if (house) {
                    setCurrentHouse(house);
                    // GetFireHouse succeeded, which only happens for a house
                    // admin — record it so the effect above skips the recheck.
                    verifiedHouseRef.current = house?.id || null;
                    setIsHouseAdmin(true);
                    sessionStorage.setItem('currentHouse', JSON.stringify(house));
                } else {
                    logger.log('[Auth] House data is null, but success is true - proceeding anyway');
                    setCurrentHouse(null);
                    sessionStorage.removeItem('currentHouse');
                }
                return { success: true, house };
            } else {
                logger.error('Get house failed:', data);
                return {
                    success: false,
                    error: getErrorMessage(data, 'Failed to load house data')
                };
            }
        } catch (error) {
            logger.error('Get house error:', error);
            return {
                success: false,
                error: 'Network error. Please try again.'
            };
        }
    };

    // Get house data for HouseOnly accounts
    const getHouseOnly = async (org) => {
        try {
            const currentKey = authKey || sessionStorage.getItem('authKey');
            const requestData = {
                Key: currentKey,
                OrgId: org.orgId,
                HouseId: org.orgId // For HouseOnly, orgId is also the HouseId
            };

            logger.debug('[Auth] getHouseOnly request');   // never the body: it carries the session Key

            const response = await fetch('/api/portal/firehouse/GetFireHouse', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestData)
            });

            const rawData = await response.json();
            const data = normalizeKeys(rawData);

            logger.log('[Auth] getHouseOnly response:', data);

            if (isSuccessResponse(data)) {
                // House can be null - that's OK, the page will handle it
                const house = data.house;
                if (house) {
                    setCurrentHouse(house);
                    // GetFireHouse succeeded, which only happens for a house
                    // admin — record it so the effect above skips the recheck.
                    verifiedHouseRef.current = house?.id || null;
                    setIsHouseAdmin(true);
                    sessionStorage.setItem('currentHouse', JSON.stringify(house));
                } else {
                    logger.log('[Auth] House data is null, but success is true - proceeding anyway');
                    setCurrentHouse(null);
                    sessionStorage.removeItem('currentHouse');
                }
                return { success: true, house };
            } else {
                logger.error('Get house only failed:', data);
                return {
                    success: false,
                    error: getErrorMessage(data, 'Failed to load house data')
                };
            }
        } catch (error) {
            logger.error('Get house only error:', error);
            return {
                success: false,
                error: 'Network error. Please try again.'
            };
        }
    };

    // Account type enums: `AccountTypes` / `OrgAccountTypes` are imported from
    // utils/builtInApplets — the one declaration. A local copy here carried
    // SharedTrainingCal as 3 (the Sponsor ordinal) until audit 12h, so the
    // routing below sent Sponsors to the shared training calendar and let a
    // real shared training-cal login fall through to the org dashboard.

    // Select organization with smart routing
    const selectOrganization = async (org) => {
        logger.log('[Auth] Selecting organization:', {
            name: org.orgName || org.name,
            orgId: org.orgId,
            accountType: org.accountType,
            orgAccountType: org.orgAccountType,
            sharedPermissionId: org.sharedPermissionId
        });

        setOrgSelectionError(null); // Clear any previous errors
        setCurrentOrg(org);
        sessionStorage.setItem('currentOrg', JSON.stringify(org));

        // Ensure authenticated state is set
        setIsAuthenticated(true);

        // Ensure user is set
        const userId = user?.id || sessionStorage.getItem('userId');
        if (userId && !user) {
            setUser({ id: userId });
        }

        // A house-scoped account — a HouseOnly org, or a shared station login —
        // never reaches the org dashboard or the org sidebar, and no house
        // surface reads `orgApplets` or `roles` (the house menu comes from the
        // static houseNav list). Fetching them here made sign-in wait on two
        // round trips whose results are then thrown away, on top of the
        // GetFireHouse call it genuinely needs. That was most of the lag before
        // the house appeared. Owner report 2026-08-08.
        const houseScoped =
            org.orgAccountType === OrgAccountTypes.HouseOnly
            || org.accountType === AccountTypes.SharedHouse;

        // Get org roles and applets — in PARALLEL.
        //
        // They were awaited one after the other, so every sign-in waited for the
        // sum of two round trips. Neither depends on the other: getOrgRoles reads
        // the org's role list, fetchOrgApplets reads its applets, and each sets
        // its own state. Overlapping them makes sign-in wait for the slower of
        // the two rather than both. PM 2026-08-08: "not fetching everything all
        // of the time" — this is the same call count, taking half the time.
        //
        // `allSettled`, not `all`: each already handles its own failures and
        // returns rather than throwing, and one failing must not abort the other
        // or the routing that follows.
        if (org.orgId && !houseScoped) {
            logger.log('[Auth] Fetching roles and applets for orgId:', org.orgId);
            const currentKey = authKey || sessionStorage.getItem('authKey');
            logger.log('[Auth] About to fetch applets with key:', currentKey ? 'present' : 'missing');
            await Promise.allSettled([
                getOrgRoles(org.orgId),
                fetchOrgApplets(currentKey, org.orgId),
            ]);
        } else if (houseScoped) {
            logger.log('[Auth] House-scoped account - skipping org roles/applets');
        } else {
            logger.log('[Auth] No orgId found, skipping roles and applets fetch');
        }

        // Route based on account type
        try {
            // Check UserAccountTypes first
            if (org.accountType === AccountTypes.SharedTrainingCal) {
                logger.log('[Auth] Routing to /trainingcalshared (SharedTrainingCal account)');
                router.push('/trainingcalshared');
                return;
            }

            if (org.accountType === AccountTypes.SharedHouse) {
                logger.log('[Auth] SharedHouse account - fetching house data...');
                const result = await getHouse(org);
                if (result.success) {
                    // → /househome, the card board, NOT /firehouseadmin. A shared
                    // station login belongs to exactly one house, so the house
                    // list was never a meaningful destination — firehouseadmin
                    // immediately `router.replace('/houseadmin')`s these accounts,
                    // dropping the crew into an admin settings form on first
                    // login. Owner direction 2026-08-08: they should land on the
                    // same card board a HouseOnly account gets.
                    logger.log('[Auth] House request successful, routing to /househome');
                    router.push('/househome');
                } else {
                    logger.error('[Auth] Failed to load house data:', result.error);
                    setOrgSelectionError(`Failed to load house data: ${result.error}`);
                    // Clear the org selection since we can't load it properly
                    setCurrentOrg(null);
                    sessionStorage.removeItem('currentOrg');
                    // Stay on memberships page so user can try again or select different org
                    router.push('/memberships');
                }
                return;
            }

            // Check OrgAccountTypes - HouseOnly (3)
            if (org.orgAccountType === OrgAccountTypes.HouseOnly) {
                logger.log('[Auth] HouseOnly account - fetching house data...');
                const result = await getHouseOnly(org);
                if (result.success) {
                    logger.log('[Auth] House request successful, routing to /househome');
                    router.push('/househome');
                } else {
                    logger.error('[Auth] Failed to load house data:', result.error);
                    setOrgSelectionError(`Failed to load house data: ${result.error}`);
                    // Clear the org selection since we can't load it properly
                    setCurrentOrg(null);
                    sessionStorage.removeItem('currentOrg');
                    // Stay on memberships page so user can try again or select different org
                    router.push('/memberships');
                }
                return;
            }

            // GroupApplet (4) accounts land on '/' like standard orgs. Legacy had
            // a separate /grouphome, but that page is the older duplicate of
            // Index.razor — Index already handles GroupApplet by swapping the
            // layout in place rather than navigating away. The Sidebar renders
            // the reduced Group menu.
            if (org.orgAccountType === OrgAccountTypes.GroupApplet) {
                logger.log('[Auth] GroupApplet account - routing to /');
                router.push('/');
                return;
            }

            // Default routing - go to home page for standard accounts
            // This includes: Unset (0), Organization (1), Private (2), Sponsor (5)
            logger.log('[Auth] Standard account - routing to /');
            router.push('/');
        } catch (error) {
            logger.error('[Auth] Error during organization selection routing:', error);
            setOrgSelectionError(`Error selecting organization: ${error.message}`);
            router.push('/');
        }
    };

    // Get organization roles
    const getOrgRoles = async (orgId) => {
        logger.log('[Auth] getOrgRoles called for orgId:', orgId);
        try {
            const currentKey = authKey || sessionStorage.getItem('authKey');
            const response = await fetch('/api/auth/getorgroles', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    Key: currentKey,
                    OrgId: orgId
                })
            });

            const rawData = await response.json();
            const data = normalizeKeys(rawData);
            logger.log('[Auth] getOrgRoles response:', data);

            if (isSuccessResponse(data)) {
                setRoles(data.roles || []);
                logger.log('[Auth] Roles loaded:', data.roles || []);
            } else {
                logger.warn('[Auth] getOrgRoles unsuccessful:', getErrorMessage(data));
            }
        }

        catch (error)  {
            logger.error('[Auth] Get roles error:', error);
        }
    };

    // Logout
    const logout = useCallback(() => {
        logger.log('[Auth] Logging out...');
        setUser(null);
        setAuthKey('');
        setOrganizations([]);
        setCurrentOrg(null);
        setCurrentHouse(null);
        setIsHouseAdmin(false);
        verifiedHouseRef.current = null;
        setRoles([]);
        setOrgApplets([]);
        setIsAuthenticated(false);
        setOrgSelectionError(null);

        // Clear all session storage
        sessionStorage.removeItem('authKey');
        sessionStorage.removeItem('userId');
        sessionStorage.removeItem('pendingUserId');
        sessionStorage.removeItem('currentOrg');
        sessionStorage.removeItem('currentHouse');
        sessionStorage.removeItem('orgApplets');
        sessionStorage.removeItem('organizations');

        logger.log('[Auth] Logout complete');
    }, []);

    // ── Session expiry ──────────────────────────────────────────────────────
    // The server answers a stale key with `errorCode: 7` on EVERY endpoint, and
    // until now each call site decided what to do about it — which mostly meant
    // logging it and carrying on, leaving a page that looked signed in but did
    // nothing. `utils/sessionExpiry` collects those reports; this is the one
    // place that acts on them. See OUTSTANDING_ITEMS.md §C2.
    useEffect(() => {
        return onSessionEnded(() => {
            // A single message, because the latch guarantees a single call even
            // when a dashboard's worth of requests expire together.
            toast.error('Your session has ended. Please sign in again.');
            try { logout(); } catch { /* clearing state must not block the redirect */ }
            router.replace('/login');
        });
    }, [logout, router]);

    // Re-arm the latch whenever a key is established — a fresh sign-in, or a
    // reload that restored one from sessionStorage — so the NEXT expiry is acted
    // on. Deliberately keyed on a truthy value: `logout` empties `authKey`, and
    // resetting on that would re-open the latch while the redirect is still in
    // flight, letting a late-returning request fire a second sign-out.
    useEffect(() => {
        if (authKey) resetSessionEnded();
    }, [authKey]);

    // Resend the two-factor code to a chosen contact method. Legacy
    // Login.razor.cs ResendAuthentication: POST lacsso/resendtwofactorcode with
    // { Id, SecToken, Method } (Models/TwoFactor.cs TwoFactorRequest). The
    // controller issues a NEW SecToken with the new code, so the returned Key
    // replaces the pending one — submitting the code against the old key would
    // fail. `Id` is the username captured at sign-in (sessionStorage
    // pendingUserId, the same slot submitTwoFactorCode reads).
    const resendTwoFactorCode = async (method) => {
        try {
            const pendingUserId = sessionStorage.getItem('pendingUserId');
            const response = await fetch('/api/auth/resendtwofactorcode', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    Id: pendingUserId,
                    SecToken: authKey,
                    Method: method
                })
            });

            const rawData = await response.json();
            const data = normalizeKeys(rawData);

            if (isSuccessResponse(data)) {
                setAuthKey(data.key);
                return {
                    success: true,
                    twoFactorMethod: data.twoFactorMethod,
                    availableMethods: data.availableMethods
                };
            } else {
                return {
                    success: false,
                    error: getErrorMessage(data, 'Could not resend the verification code'),
                    response: data
                };
            }
        } catch (error) {
            logger.error('Resend 2FA code error:', error);
            return {
                success: false,
                error: 'Network error. Please try again.',
                response: error
            };
        }
    };

    // Request password reset. `method` is the optional contact method for a
    // resend (legacy ResendResetRequest sets ResetPasswordRequest.Method; the
    // controller falls back to the default contact when it is empty).
    const requestPasswordReset = async (userId, method) => {
        try {
            const response = await fetch('/api/auth/requestpasswordreset', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(method ? { UserId: userId, Method: method } : { UserId: userId })
            });

            const rawData = await response.json();
            const data = normalizeKeys(rawData);

            if (isSuccessResponse(data)) {
                return {
                    success: true,
                    resetMethod: data.resetMethod,
                    availableMethods: data.availableMethods
                };
            } else {
                return {
                    success: false,
                    error: getErrorMessage(data, 'Password reset request failed')
                };
            }
        } catch (error) {
            logger.error('Password reset request error:', error);
            return {
                success: false,
                error: 'Network error. Please try again.'
            };
        }
    };

    // Submit password reset
    const submitPasswordReset = async (userId, resetCode, newPassword) => {
        try {
            const response = await fetch('/api/auth/submitpasswordresetreply', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    UserId: userId,
                    PassCode: resetCode,
                    NewPw: newPassword
                })
            });

            const rawData = await response.json();
            const data = normalizeKeys(rawData);

            if (isSuccessResponse(data)) {
                return { success: true };
            } else {
                return {
                    success: false,
                    error: getErrorMessage(data, 'Password reset failed')
                };
            }
        } catch (error) {
            logger.error('Password reset error:', error);
            return {
                success: false,
                error: 'Network error. Please try again.'
            };
        }
    };

    // Verify new account email
    const verifyNewAccountEmail = async (email) => {
        try {
            const response = await fetch('/api/auth/verifynewaccountemail', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ContactString: email })
            });

            const rawData = await response.json();
            const data = normalizeKeys(rawData);

            if (isSuccessResponse(data)) {
                return { success: true };
            } else {
                return {
                    success: false,
                    error: getErrorMessage(data, 'Email verification failed')
                };
            }
        } catch (error) {
            logger.error('Email verification error:', error);
            return {
                success: false,
                error: 'Network error. Please try again.'
            };
        }
    };

    // Create new account
    const createNewAccount = async (accountData) => {
        try {
            const requestPayload = {
                UserId: accountData.email,
                FirstName: accountData.firstName,
                LastName: accountData.lastName,
                // The modal passes `passCode` — the one-time code emailed by
                // verifynewaccountemail. This read `accountData.verificationCode`,
                // a key nothing ever sets, so PassCode went out undefined, was
                // dropped from the JSON, and the server's
                // `string.IsNullOrEmpty(request.PassCode)` guard answered
                // "Invalid request". Account creation had NEVER worked in this
                // portal. Reported by a live client 31 Aug 2026.
                PassCode: accountData.passCode,
                Pw: accountData.password,
                AppId: 'FireOps Portal',
                Platform: 'WEB',
                Device: 'undefined',
                Ip: 'undefined'
            };

            const response = await fetch('/api/auth/CreateNewAccount', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestPayload)
            });

            const rawData = await response.json();
            const data = normalizeKeys(rawData);

            if (isSuccessResponse(data)) {
                return { success: true };
            } else {
                return {
                    success: false,
                    error: getErrorMessage(data, 'Account creation failed')
                };
            }
        } catch (error) {
            logger.error('Create account error:', error);
            return {
                success: false,
                error: 'Network error. Please try again.'
            };
        }
    };

    // Drop the selected org (and its org-scoped state) while staying logged in,
    // so the user lands back on the org picker rather than the login screen —
    // used after leaving an org when other memberships remain.
    const clearCurrentOrg = useCallback(() => {
        setCurrentOrg(null);
        setCurrentHouse(null);
        setIsHouseAdmin(false);
        verifiedHouseRef.current = null;
        setRoles([]);
        setOrgApplets([]);
        sessionStorage.removeItem('currentOrg');
        sessionStorage.removeItem('currentHouse');
        sessionStorage.removeItem('orgApplets');
    }, []);

    // Merge updated fields into currentOrg and sync sessionStorage.
    // Call this after any admin save that changes org-level flags the sidebar reads.
    const updateCurrentOrg = useCallback((updatedFields) => {
        setCurrentOrg(prev => {
            if (!prev) return prev;
            const merged = { ...prev, ...updatedFields };
            sessionStorage.setItem('currentOrg', JSON.stringify(merged));
            return merged;
        });
    }, []);

    // Check permissions based on org and role
    const hasPermission = useCallback((permission) => {
        if (!currentOrg) return false;

        // Admin has all permissions
        if (currentOrg.isAdmin) return true;

        // Check appletAdminPermissions for specific applet access
        if (currentOrg.appletAdminPermissions?.includes(permission)) {
            return true;
        }

        // Check role-based permissions
        switch (permission) {
            case 'manage_boards':
            case 'manage_sites':
            case 'manage_tags':
                return currentOrg.isAdmin || roles.includes('Manager');
            case 'manage_personnel':
                return currentOrg.isAdmin || roles.includes('Manager') || roles.includes('HR');
            case 'view_reports':
                return true; // All authenticated users can view
            default:
                return false;
        }
    }, [currentOrg, roles]);

    // Check if specific module is hidden (camelCase: hideChannelPlans, hideEventCalendar, etc.)
    const isModuleHidden = useCallback((moduleName) => {
        if (!currentOrg) return false;

        // Convert module name to camelCase hide property
        // e.g., "ChannelPlans" -> "hideChannelPlans"
        const hideProperty = `hide${moduleName}`;
        return currentOrg[hideProperty] === true;
    }, [currentOrg]);

    // ── House admin permission ──────────────────────────────────────────────
    // Whether the signed-in account administers the CURRENT house. Both house
    // sidebars use it to decide whether to offer "House Admin" at all.
    //
    // The server is the only authority: `checkappletpermissions` with
    // AppletId "firehouse" and the house id is exactly what the Portal API's
    // VerifyAdmin calls, so the menu and the API agree by construction rather
    // than by us re-implementing the rule on the client.
    //
    // `verifiedHouseRef` avoids a redundant round trip: `getHouse` and
    // `getHouseOnly` ALREADY prove admin rights, because GetFireHouse answers
    // Unauthorized to everyone else. So a house login costs nothing extra; only
    // a portal user drilling into a house from the list triggers the check.
    const verifiedHouseRef = useRef(null);
    const [isHouseAdmin, setIsHouseAdmin] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const houseId = currentHouse?.id;
        const orgIdForCheck = currentOrg?.orgId;
        const key = authKey || (typeof window !== 'undefined' ? sessionStorage.getItem('authKey') : null);

        if (!houseId || !orgIdForCheck || !key) { setIsHouseAdmin(false); return; }
        if (verifiedHouseRef.current === houseId) { setIsHouseAdmin(true); return; }

        (async () => {
            try {
                const res = await fetch('/api/auth/checkappletpermissions', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        Key: key, OrgId: orgIdForCheck,
                        AppletId: 'firehouse', ObjectId: houseId,
                    }),
                });
                const data = res.ok ? readApiJson(await res.json()) : null;
                const ok = !!data?.success;
                if (!cancelled) {
                    setIsHouseAdmin(ok);
                    if (ok) verifiedHouseRef.current = houseId;
                }
            } catch {
                // Fail CLOSED: if we cannot confirm, do not offer the link. The
                // page and every write endpoint re-check regardless, so the worst
                // case is a hidden menu item, not an unguarded one.
                if (!cancelled) setIsHouseAdmin(false);
            }
        })();

        return () => { cancelled = true; };
    }, [currentHouse?.id, currentOrg?.orgId, authKey]);

    // Explicitly select a house (e.g. from the /firehouseadmin list) and persist it.
    // This is the Next.js equivalent of Blazor's `Auth.CurrentHouse = house`.
    const selectHouse = useCallback((house) => {
        setCurrentHouse(house);
        if (house) {
            sessionStorage.setItem('currentHouse', JSON.stringify(house));
        } else {
            sessionStorage.removeItem('currentHouse');
        }
    }, []);

    const value = {
        user,
        loading,
        isAuthenticated,
        isAuthenticating,
        authKey,
        organizations,
        currentOrg,
        currentHouse,
        isHouseAdmin,
        roles,
        orgApplets,
        appletsLoading,
        orgSelectionError,
        login,
        logout,
        submitTwoFactorCode,
        resendTwoFactorCode,
        selectOrganization,
        refreshOrganizations,
        updateCurrentOrg,
        clearCurrentOrg,
        requestPasswordReset,
        submitPasswordReset,
        verifyNewAccountEmail,
        createNewAccount,
        hasPermission,
        isModuleHidden,
        getHouse,
        getHouseOnly,
        selectHouse,
        refreshApplets
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};